import { useState, type JSX } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useLiveQuery } from "dexie-react-hooks";
import { CreateShoppingListRequestSchema, type RecipeApiItem, type ShoppingListApiItem } from "@recipevault/shared";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/ui/sheet.js";
import { Button } from "@/ui/button.js";
import { db } from "../../sync/db.js";
import { sync } from "../../sync/SyncService.js";
import { friendlyError } from "../../shared/http.js";
import { formatFactor, formatQty } from "./ingredientFormat.js";

// `CreateShoppingListRequestSchema.name`'s max is the source of truth for how long a
// new list's name may be; `recipe.title` (TitleSchema) allows up to 200 chars, well past
// it, so "+ New list" must truncate or every long-titled recipe fails server validation.
// Read live off the schema (via zod4's public `.maxLength` getter) so this can never drift.
const NEW_LIST_NAME_MAX = CreateShoppingListRequestSchema.shape.name.maxLength ?? 120;

/**
 * Clamp a recipe title down to a valid shopping-list name: trims, then truncates to the
 * schema's max length. Falls back to a generic name only in the degenerate case where the
 * title is entirely whitespace (slicing never produces an empty string otherwise, since
 * TitleSchema already enforces a min length of 1 on `recipe.title`).
 */
export function toListName(title: string): string {
  const trimmed = title.slice(0, NEW_LIST_NAME_MAX).trim();
  return trimmed.length > 0 ? trimmed : "Shopping list";
}

export interface SendToShoppingSheetProps {
  recipe: RecipeApiItem;
  /** Servings-scale multiplier from Task 12's `useServingsScale` (1 = as written). */
  factor: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Bottom sheet for "send this recipe's ingredients to a shopping list" (Plan 13 Task 13).
 * Every ingredient row starts checked; unchecking any of them switches the dispatch from
 * a single server-aggregated call (recipe provenance preserved) to sequential per-item
 * calls (provenance intentionally dropped — see the dispatch comment below).
 */
export function SendToShoppingSheet({ recipe, factor, open, onOpenChange }: SendToShoppingSheetProps): JSX.Element {
  const navigate = useNavigate();
  const [checked, setChecked] = useState<Set<string>>(() => new Set(recipe.ingredients.map((i) => i.id)));
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lists = useLiveQuery(() => db.shoppingLists.toArray(), []);
  // Same "most recently updated active list" rule as useTonight's activeShoppingListId.
  const activeLists = (lists ?? [])
    .filter((l) => !l.deletedAt && l.status === "active")
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
  const effectiveListId = selectedListId ?? activeLists[0]?.id ?? null;

  const checkedCount = checked.size;
  const allChecked = checkedCount === recipe.ingredients.length;

  function reset(): void {
    setChecked(new Set(recipe.ingredients.map((i) => i.id)));
    setSelectedListId(null);
  }

  function handleOpenChange(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  function toggleChecked(id: string): void {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * ALL rows checked -> one server-aggregated call keyed off the recipe (provenance
   * preserved). Any subset -> sequential per-item calls carrying only name/qty/unit;
   * this loses recipe provenance on the resulting items, which is an accepted
   * trade-off (cross-cutting review made recipe provenance optional) rather than a
   * bug — do not try to smuggle partial fromRecipeIds through here.
   */
  async function dispatch(listId: string): Promise<ShoppingListApiItem> {
    if (allChecked) {
      return sync.addItemOptimistic(listId, {
        fromRecipeIds: [recipe.id],
        scaleByServings: { [recipe.id]: factor },
      });
    }
    let last: ShoppingListApiItem | undefined;
    for (const ing of recipe.ingredients) {
      if (!checked.has(ing.id)) continue;
      last = await sync.addItemOptimistic(listId, {
        name: ing.name,
        qty: ing.qty === null ? undefined : ing.qty * factor,
        unit: ing.unit ?? undefined,
      });
    }
    // checkedCount > 0 is guaranteed by the caller's disabled-button guard, so `last`
    // is always assigned by the loop above.
    return last!;
  }

  function announceSuccess(list: ShoppingListApiItem): void {
    toast.success(`Added ${checkedCount} item${checkedCount === 1 ? "" : "s"} to "${list.name}"`, {
      action: {
        label: "Open list →",
        onClick: () => { void navigate(`/shopping/${list.id}`); },
      },
    });
  }

  async function handleAdd(): Promise<void> {
    if (busy || checkedCount === 0 || !effectiveListId) return;
    setBusy(true);
    try {
      const list = await dispatch(effectiveListId);
      announceSuccess(list);
      handleOpenChange(false);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateAndAdd(): Promise<void> {
    if (busy || checkedCount === 0) return;
    setBusy(true);
    try {
      const created = await sync.createShoppingListOptimistic({ name: toListName(recipe.title) });
      setSelectedListId(created.id);
      const list = await dispatch(created.id);
      announceSuccess(list);
      handleOpenChange(false);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <SheetHeader>
          <SheetTitle>Send to shopping list</SheetTitle>
          <SheetDescription>
            {factor !== 1 ? `Quantities scaled ×${formatFactor(factor)}.` : "Pick what to add."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-1.5 px-4">
          <span className="text-sm font-medium text-zinc-300">Add to</span>
          {activeLists.length === 0 && <p className="text-sm text-zinc-500">No active lists yet — start a new one.</p>}
          {activeLists.map((list) => {
            const total = list.items.length;
            return (
              <label
                key={list.id}
                className="flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 cursor-pointer hover:bg-zinc-800/60"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <input
                    type="radio"
                    name="send-to-shopping-target"
                    checked={effectiveListId === list.id}
                    onChange={() => { setSelectedListId(list.id); }}
                    className="accent-emerald-500 size-5 shrink-0"
                  />
                  <span className="font-medium text-zinc-100 truncate">{list.name}</span>
                </span>
                <span className="text-xs text-zinc-500 shrink-0">
                  {total === 0 ? "No items" : `${total} item${total === 1 ? "" : "s"}`}
                </span>
              </label>
            );
          })}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start mt-1"
            disabled={busy || checkedCount === 0}
            onClick={() => { void handleCreateAndAdd(); }}
          >
            + New list
          </Button>
        </div>

        <div className="flex flex-col gap-1.5 px-4 mt-2">
          <span className="text-sm font-medium text-zinc-300">Ingredients</span>
          <ul className="flex flex-col gap-1.5">
            {recipe.ingredients.map((ing) => {
              const scaledQty = ing.qty === null ? null : ing.qty * factor;
              const amount = [scaledQty === null ? "" : formatQty(scaledQty), ing.unit ?? ""]
                .filter(Boolean)
                .join(" ");
              const isChecked = checked.has(ing.id);
              return (
                <label
                  key={ing.id}
                  className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 cursor-pointer hover:bg-zinc-800/60"
                >
                  <input
                    type="checkbox"
                    aria-label={ing.name}
                    checked={isChecked}
                    onChange={() => { toggleChecked(ing.id); }}
                    className="accent-emerald-500 size-5 shrink-0"
                  />
                  <span
                    className="shrink-0 w-16 text-right font-medium text-zinc-100 tabular-nums"
                    aria-hidden={amount === "" || undefined}
                  >
                    {amount}
                  </span>
                  <span className="flex-1 min-w-0 text-zinc-300">{ing.raw?.trim() || ing.name}</span>
                </label>
              );
            })}
          </ul>
        </div>

        <SheetFooter>
          <Button
            type="button"
            size="cook"
            onClick={() => { void handleAdd(); }}
            disabled={busy || checkedCount === 0 || !effectiveListId}
          >
            {busy ? "Adding…" : `Add ${checkedCount} item${checkedCount === 1 ? "" : "s"}`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
